// ============================================================================
// ui/mobile-kind-pager.js — beat 2 on a phone: one product per screen, swiped
// ============================================================================
// Ryan, 2026-07-26: "once you click on the card(s), they snap around a bit… for
// mobile the best thing would be to have them arrange themselves into a line
// from left to right including what you've selected and then they expand down
// taking up the full screen and it turns into a swipe left swipe right feature
// so you have to swipe left and swipe right to see the individual items under
// each category which you then select before clicking into the actual designer."
//
// ── WHY THIS IS A SEPARATE SURFACE, NOT A BRANCH IN select-weights-step ─────
// That file is ~900 lines of FLIP choreography — pinning cards, collapsing them,
// measuring first/last boxes, swapping chip rows — all tuned for the desktop row
// and signed off. Threading a pager through it would mean every future desktop
// change has to keep a phone layout working that shares none of its assumptions,
// and the snapping Ryan is describing IS that choreography running in a 2×2 grid
// it was never designed for. So on a phone the grid hands over: no FLIP, no
// pinning, no collapse — a different surface for a different shape of screen.
//
// ── SWIPE IS CSS, NOT JAVASCRIPT ────────────────────────────────────────────
// `scroll-snap-type: x mandatory` with one full-width child per item gives real
// momentum, rubber-banding, an interruptible fling and VoiceOver support for
// free, all at the platform's own frame rate. A JS drag handler reimplements
// those badly — it is the single most common way a custom carousel ends up
// feeling cheap next to the OS.
//
// The category is a HEADING on each page rather than a row of tabs, because the
// pages already run in category order: the heading changes as you cross a
// boundary, which teaches the grouping without spending vertical space on it.
// ============================================================================

import { h, replaceChildren } from './dom.js';
import { toggleProductSelection, isProductSelected } from './state.js';
import { createConfigAdder, configAnchor } from './config-adder.js';
import { createDenominationRow } from './denomination-row.js';
import { CATEGORIES, productKey, modelFor, geomOptsFor, defaultWeightFor, productSupportsBodyColor, productSupportsMultiConfig } from '../products.js';
import { createDefaultProfile, DEFAULT_BODY_COLOR } from '../design-profile.js';
import { renderDesignToSVGString } from '../render-engine.js';

// ── THERE IS NO CARD ZOOM, AND THERE NEVER WAS (2026-07-26) ─────────────────────────────────────
// A `CARD_ZOOM` table used to dolly each freshly-built reveal in by 1.28–1.7 right after `open()`.
// It never survived: `fitCamera()` does `camera.position.set(0, 0, dist)`, so it does not adjust
// the framing, it REPLACES it — and one always runs immediately after, because `build()` registers
// a ResizeObserver whose first observation is delivered whether or not the box changed. Measured at
// rest: the head spans 81.5% of the canvas, i.e. `FILL_FRACTION` (0.82) exactly, with no trace of a
// 1.7×. What the dolly did do was paint a frame or two at 1.7× on a device fast enough to get them
// on screen before that first observation landed — Ryan 2026-07-26: "it's like a rapid zoom out
// effect… the model is big and almost taking up the full window then zooms out and settles." Only
// ever on the FIRST card, because every other model is built inside the off-screen oven where those
// frames are invisible.
//   So the dolly is gone and the resting size is unchanged (it was already fit framing). If the
// cards should be tighter, the multiplier has to go INSIDE `fitCamera` — anything applied after it
// is written over by the next re-fit.
// At most this many live contexts. The STILLS carry every card visually, so a reveal is only needed
// for the card you have landed on — a small ring keeps back-and-forth instant without holding ten.
//   THREE → TWO (2026-07-27). Every remaining symptom on Ryan's phone points at context pressure:
// builds refused outright, and contexts EVICTED mid-interaction ("it lagged briefly then
// disappeared and all I could see was the gray background"). A browser over its per-page limit does
// not queue or refuse politely, it takes one away, and the one it takes is not ours to choose. The
// ring is a latency optimisation for swiping BACK one card; the stills already cover that visually,
// so it was buying a little smoothness with the one resource that was running out.
const MAX_LIVE = 2;

// ── THE CARD IS NOT THE STAGE, SO IT DOES NOT NEED THE STAGE'S DETAIL ──────────────────────────────
// Both of these attack the same measured number: the canvas element exists ~310ms into a build and
// the first frame does not reach it until ~2.1s. Nothing else runs in that gap — not the swipe, not
// the wheel — because the geometry build is synchronous on the main thread. Ten products means ten
// of those, and swiping while one is running is what Ryan hit as "it takes 5-10 seconds".
//
// The designer's tessellation is calibrated for a full-screen stage a buyer can zoom to 500%. This
// card is ~330 CSS px of gently-swaying thumbnail, so the same numbers are paying for detail that
// cannot land on a pixel. Halving them roughly halves the build. The one that matters most is
// `shaftAxialSegments`: it only does work when `shaftOffset` is a function — i.e. the EZ-curl bend
// — which is why the EZ bars were the heaviest geometry in the set at ~30k triangles.
const CARD_DETAIL = Object.freeze({
  plate: Object.freeze({ radialSegments: 72, filletSegments: 2, boltSegments: 12 }),
  grip: Object.freeze({ radialSegments: 90, edgeSegments: 4 }),   // clamped to ≥48 internally
  dumbbell: Object.freeze({ radialSegments: 48, edgeSegments: 4, faceCornerSegs: 2, shaftAxialSegments: 24 }),
  barbell: Object.freeze({ radialSegments: 48, edgeSegments: 4, faceCornerSegs: 2, shaftAxialSegments: 24 }),
});
// The face texture, likewise: 1024 was the designer's number. The card shows it at ~330 CSS px —
// ~500 device px at the capped DPR of 1.5 — so 512 is still over-sampled, and it quarters both the
// SVG rasterize and the GPU upload that the first frame is waiting on.
const CARD_TEXTURE_PX = 512;

// ── THE POSE THAT IDENTIFIES THE PRODUCT (Ryan 2026-07-27) ────────────────────────────────────────
// This began as a request for an ENTRANCE — "the first time you swiped onto a product, it started
// turned 90 degrees, then on load it spins that quarter turn… which isn't always evident with
// products like the fixed barbells." The animation was built and then removed on sight: "it's
// jittery and jumpy and laggy." It could not have been otherwise, and the reason is worth keeping.
// An entrance tied to LOAD animates in the one window where the main thread is busy with a
// synchronous geometry build — the worst frame budget the app ever has. Any load-time flourish here
// inherits that; the fix is not smoother easing, it is not animating then.
//
// What survived is the observation UNDER the request, which was the valuable part: a bar framed
// head-on shows you an END CAP. A black disc, near-identical to the bumper plate on the next card,
// with nothing about it that says "barbell" — and no animation could have helped the nine cards you
// are NOT on, or the sliver of the next one peeking past the edge, or the still. So the RESTING pose
// changes per product: face-on for a plate or grip, where the branded face is the product, and
// turned for the long ones, where the silhouette is what names them. Static, it is always true.
//   Kept small. A bar's head sits half a bar-length from its centre, so the turn is doing its work
// through foreshortening of the shaft, not through swinging the head around — and the shaft running
// out of frame is how a close product shot of a barbell looks anyway.
const REST_YAW_DEG = { plate: 0, grip: 0, dumbbell: 24, barbell: 26 };
// …and the turn alone does NOT do it. `fitCamera` frames the branded FACE to fill the view, which
// on a bar means the head fills the card and the shaft is off-screen at every angle — measured: at
// a 26 degree yaw the bar still read as a hockey puck. Ryan's other instinct was the answer ("should
// there be an automatic zoom out"), but as a per-product RESTING framing rather than something that
// animates: a dolly during the turn would have to travel ~20x to bring a 4-foot bar into frame, and
// at the wide end the branded face — the thing being chosen — is unreadable. Static, it costs
// nothing, and it fixes the STILL and the peeking neighbour too, which no animation can reach.
const FIT_SCALE = { plate: 1, grip: 1, dumbbell: 0.86, barbell: 0.78 };
// WHERE each product turns, as a fraction of the way from its middle to its branded face.
//   A bar is far longer than the frame, so only its head and a run of shaft are ever visible: turn
// it about the middle and the head leaves the screen, turn it about the head and the whole product
// hangs off one side. 0.8 lands the axis a few inches INBOARD of the head — Ryan's own suggestion —
// which holds the head on screen AND centres what you can see.
//   A dumbbell is short enough that BOTH heads fit, so its own middle is already the centre of the
// composition and there is nothing to correct; 0 is the plain centre pivot every other caller uses.
const FACE_PIVOT = { plate: 0, grip: 0, dumbbell: 0.72, barbell: 0.89 };
// The BOB — the up/down nod — off entirely for the long products. Rotating a bar a couple of
// degrees about X swings its far end through an arc as long as the bar, and the contact shadow
// stretches and fades with it; the eye reads that as the whole thing bouncing. Left/right is kept:
// a bar turning slowly on the spot looks alive, a bar nodding looks like it is being dribbled.
const IDLE_BOB_SCALE = { plate: 1, grip: 1, dumbbell: 0, barbell: 0 };
const DEG = Math.PI / 180;

const STILL_POSE_MS = 900;
// `--dt-duration-slide` (0.35s), plus a frame's grace. The still must not be taken mid-entrance.
const ENTRANCE_MS = 400;

const CHECK =
  '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.4" ' +
  'stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>';
// Its own copy, heavier and blunter: at 9px the 2.4 stroke above thins to nothing and the tick
// reads as a smudge. `currentColor` so the dot's own state decides whether it is blue or white.
const DOT_CHECK =
  '<svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="currentColor" stroke-width="4.5" ' +
  'stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>';

/**
 * Build the pager for every kind under the categories the buyer picked.
 *
 * @param {object} ctx           the app context (needs `.state`)
 * @param {Array}  selectedCats  category ids, in CATEGORIES order
 * @param {(n:number)=>void} onChange fires with the running selection count
 * @param {{holdUntil?: Promise<any>}} [opts] `holdUntil` defers every GEOMETRY BUILD (and only
 *   those) until it settles — see the note by `loadChunks`. The caller owns its release.
 * @returns {{el: HTMLElement, refresh: () => void}}
 */
export function createMobileKindPager(ctx, selectedCats, onChange, opts = {}) {
  const { state } = ctx;
  // Flattened in CATEGORIES order so the pages run in the same order as the row of cards the buyer
  // just came from — the sequence has to match what they saw, or the swipe feels arbitrary.
  const items = [];
  for (const cat of CATEGORIES) {
    if (!selectedCats.includes(cat.id)) continue;
    for (const kind of cat.kinds) items.push({ category: cat.id, categoryLabel: cat.label, one: cat.one, kind });
  }

  // ── THE CATEGORY HEADER (Ryan 2026-07-26) ────────────────────────────────────────────────────
  // "the blue header… is a nice touch, but I don't want it to slide when you slide the images… it
  // should stay constant. Then… once the first from the new group lands, if scrolling left to right,
  // the header should slide up and disappear as a new one appears and slides up from the bottom."
  //
  // So it leaves the pages entirely. A per-page copy travels with the scroll by definition — the
  // header is a property of WHERE YOU ARE, not of the page under your thumb, and lifting it out is
  // what lets it hold still and then move on its own terms. The direction carries the meaning:
  // moving forward pushes the old label up and brings the new one up from below, so the motion
  // agrees with the travel instead of just announcing a change.
  const catHead = h('div', { class: 'wd-kp__cathead' });
  const track = h('div', { class: 'wd-kp__track' });
  const dots = h('div', { class: 'wd-kp__dots', 'aria-hidden': 'true' });
  const counter = h('div', { class: 'wd-kp__counter', 'aria-live': 'polite' });
  const el = h('div', { class: 'wd-kp' }, catHead, track, h('div', { class: 'wd-kp__foot' }, dots, counter));

  const pages = items.map((it, i) => {
    /* ⛔ A STABLE INNER SPAN, because `paint()` rewrites this label on every tap and the condense
       has to FADE it (Ryan 2026-08-10: *"the 'Selected' can fade away"*). An opacity transition on
       a node that was just replaced by `innerHTML` starts AT its target — so rewriting the button
       itself would make the word vanish on the frame rather than leave. The wrapper outlives the
       rewrite, so the fade has something continuous to run on. */
    const pickIn = h('span', { class: 'wd-kp__pick-in' });
    const pick = h('button', {
      type: 'button', class: 'wd-kp__pick',
      onClick: () => {
        toggleProductSelection(state, it.category, it.kind);
        paint();
        onChange?.(state.ui.selectedProducts.length);
      },
    }, pickIn);
    /* THE PHONE'S PILL IS ITS SELECT BUTTON, so the ± circles ride on THAT — same idiom as the two
       chip surfaces, one component, no phone-only geometry. The product name above it takes the
       "(2)" the desktop pill's label takes. ⚠ The circles are siblings of the button, never
       children: `paint()` rewrites `pick.innerHTML` wholesale on every tap and would delete them. */
    const kindEl = h('h3', { class: 'wd-kp__kind' }, it.kind);
    const adder = productSupportsMultiConfig(productKey(it.category, it.kind))
      ? (it._adder = createConfigAdder(ctx, {
        category: it.category,
        kind: it.kind,
        labelEl: kindEl,
        onChange: () => { paint(); onChange?.(state.ui.selectedProducts.length); },
      }))
      : null;
    /* The denomination circles ride the SAME anchor as the ± — they take the room the pill gives up
       when it condenses, so they have to share its box. Built only where the ± are, because "can
       hold two configurations" and "has rungs to choose between" are the same question
       (`denominationRungs` asks it once). Its `onChange` is the adder's, verbatim: a claim moving is
       the same kind of event as a claim being added, and the pager repaints identically for both. */
    const denoms = adder
      ? (it._denoms = createDenominationRow(ctx, {
        category: it.category,
        kind: it.kind,
        onChange: () => { paint(); onChange?.(state.ui.selectedProducts.length); },
      }))
      : null;
    const page = h(
      'section',
      { class: 'wd-kp__page', 'data-idx': String(i), 'aria-roledescription': 'slide' },
      // No white inner card. It used to sit here, get covered by the stage backdrop a beat later,
      // and read as a flash (Ryan 2026-07-26: "the white card disappears and the gray 3D model
      // background loads"). The art box now WEARS the studio backdrop from the first frame, so the
      // reveal paints an identical surface over an identical surface and there is nothing to see.
      h('div', { class: 'wd-kp__art' },
        // A RENDERED STILL of this page's model, on every page at all times. A canvas can only be
        // in one place, and 10 pages share 4 models — so the live one alone left every neighbouring
        // card empty while you swiped past it (Ryan 2026-07-26: "even when the new product card is
        // half on half off the mobile display… I want the 3D model to be visible"). The still is
        // the same render, so a fast flick shows product on every card and costs one <img>.
        h('img', { class: 'wd-kp__still', alt: '', 'aria-hidden': 'true' }),
        h('div', { class: 'wd-kp__loader', 'aria-hidden': 'true' })),
      kindEl,
      // No category sub-line here (Ryan 2026-07-26): the fixed header above the pager already
      // names the category, and repeating it in grey under the product name said the same thing
      // twice on the one screen where vertical space is scarcest.
      adder ? configAnchor(pick, denoms.el, adder.el) : pick,
    );
    page._pick = pick;
    page._pickIn = pickIn;
    return page;
  });
  replaceChildren(track, pages);
  // Each dot carries its tick from the start, hidden until picked — building it up front keeps
  // `paint()` a pure class toggle rather than something that rewrites markup on every tap.
  replaceChildren(dots, items.map(() => h('span', { class: 'wd-kp__dot' },
    h('span', { class: 'wd-kp__dot-tick', 'aria-hidden': 'true', html: DOT_CHECK }))));

  /** Repaint every page's selected state + the footer. Cheap; there are a handful of pages. */
  function paint() {
    items.forEach((it, i) => {
      const on = isProductSelected(state, it.category, it.kind);
      const page = pages[i];
      it._adder?.sync();
      it._denoms?.sync();
      page.classList.toggle('is-selected', on);
      page._pickIn.innerHTML = on ? `${CHECK}<span>Selected</span>` : '<span>Select weight</span>';
      page._pick.setAttribute('aria-pressed', on ? 'true' : 'false');
      /* No anchor class to set here — `createDenominationRow.sync()` owns `is-denoms`, so the pill
         condenses on exactly the frames the circles appear rather than on a count computed twice.
         ⚠ The pill also stops toggling while it is condensed (Ryan: *"no longer the ability to tap
         the middle of the pill to turn it on/off"*); that is `pointer-events` in the CSS, not a
         guard here, so the ± keep working — they are its siblings, not its children. */
      // The dot carries the pick too (Ryan 2026-07-27). The footer row is the only place that can
      // show, at a glance, WHICH of ten products you have chosen — the card in front of you already
      // says so, and the running count says how many but never which.
      const dot = dots.children[i];
      if (dot) dot.classList.toggle('is-picked', on);
    });
    syncPosition();
  }

  let shownCat = null;
  let shownIdx = 0;
  const reducedMotion = (() => {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
  })();

  /**
   * Swap the header, travelling in the direction of the swipe.
   * @param {string} label the new category
   * @param {number} dir   +1 moving forward through the pages, -1 back
   */
  function setCategory(label, dir) {
    if (label === shownCat) return;
    const incoming = h('span', { class: 'wd-kp__cattext' }, label);
    // EVERY label present, not `firstElementChild`. The old code assumed at most one was on screen,
    // which holds only if each 280ms swap finishes before the next begins. Cross two category
    // boundaries faster than that — a flick through ten products does it easily — and the one it
    // picked was the label already on its way out, while the one actually showing was never
    // animated and never removed. They pile up and render on top of each other, which is the
    // "FIXDUEMBBBAERLBLESLS" mess a fast sweep produces.
    const leaving = [...catHead.children];
    catHead.appendChild(incoming);
    shownCat = label;
    // First paint, or motion turned down: no travel, just be correct.
    if (!leaving.length || reducedMotion) { leaving.forEach((n) => n.remove()); return; }
    const D = 280;
    const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
    const out = dir > 0 ? '-110%' : '110%';   // forward → the old label leaves upward
    const from = dir > 0 ? '110%' : '-110%';  // …and the new one arrives from below
    // Only the most recent one gets to travel; anything still mid-exit behind it goes at once. Three
    // labels dissolving over each other is not a transition, it is a smear.
    leaving.slice(0, -1).forEach((n) => n.remove());
    [leaving[leaving.length - 1]].forEach((node) => {
      node.animate(
        [{ transform: 'translateY(0)', opacity: 1 }, { transform: `translateY(${out})`, opacity: 0 }],
        { duration: D, easing: EASE, fill: 'forwards' },
      ).onfinish = () => node.remove();
    });
    incoming.animate(
      [{ transform: `translateY(${from})`, opacity: 0 }, { transform: 'translateY(0)', opacity: 1 }],
      { duration: D, easing: EASE },
    );
  }

  /**
   * How far the track scrolls per page.
   *
   * NOT `track.clientWidth`. Since the peek landed, the track carries `padding-inline` and a page is
   * exactly that much narrower than the scrollport — so dividing scrollLeft by the track width
   * drifts by one peek per page and, four or five pages along, reports the wrong product entirely.
   * The page's own measured width is the stride, and it is derived rather than assumed so the CSS
   * stays free to change the peek without this having to hear about it.
   */
  function pageStride() {
    const w = pages[0] ? pages[0].getBoundingClientRect().width : 0;
    return Math.max(1, w || track.clientWidth);
  }

  /** Which page is centred — read from scroll rather than tracked, so a fling that lands between
   *  two pages still reports the one the platform snapped to. */
  function syncPosition() {
    const i = Math.round(track.scrollLeft / pageStride());
    const idx = Math.max(0, Math.min(items.length - 1, i));
    // Only ONCE THE PAGE HAS LANDED, which is what Ryan asked for — the header must not track a
    // half-completed drag, or it flickers between two labels while the thumb is still moving.
    if (items[idx]) {
      setCategory(items[idx].categoryLabel, idx >= shownIdx ? 1 : -1);
      shownIdx = idx;
      // On the SCROLL, not on `scheduleStage`'s 200ms settle: whether a card is empty is visible in
      // the DOM and needs no debounce, so the wheel is up from the first frame of a blank card.
      syncWheel();
    }
    [...dots.children].forEach((d, n) => d.classList.toggle('is-on', n === idx));
    const picked = state.ui.selectedProducts.length;
    // No swipe hint when there is nowhere to swipe TO (a category with a single kind — Grip Plates).
    const hint = picked ? ` · ${picked} selected` : (items.length > 1 ? ' · swipe to browse' : '');
    counter.textContent = `${idx + 1} of ${items.length}${hint}`;
  }

  // ── LIVE 3D on the centred page (Ryan 2026-07-26) ────────────────────────────────────────────
  // "can we use 3D models? even if it's not the actual 3D model, but like a looping video of it
  // gently/slowly oscillating in a circle or something?" — the real model, and no video needed:
  // `createReveal`'s persistent mode already idles with a slow sway, which IS the motion he
  // described, driven by the same code-generated geometry the designer uses.
  //
  // ONE canvas, MOVED. Four live WebGL contexts on a phone is exactly what the plan's mobile budget
  // (<50k tris, one 2048 texture, DPR ~1.5) is written against — and a pager only ever shows one
  // product, so one context is all the screen can use. The mount is reparented into whichever page
  // is centred; only a change of MODEL costs a rebuild.
  //
  // LAZY, and deliberately so: `reveal-3d` + the texture pipeline are the heaviest chunks in the
  // bundle, and pulling them in while the gate is still painting would trade a visibly slower
  // first screen for art nobody has looked at yet. The white card is a real placeholder — if the
  // import never resolves, that is what stays, and nothing else breaks.
  // ── LIVE 3D: one reveal PER MODEL, kept alive ────────────────────────────────────────────────
  // The first cut kept a single reveal and disposed it whenever the model changed. That is what
  // Ryan hit: "when I do load a model then swipe to the next one… if I swipe back to the previous
  // one which I was just on earlier, it then has to load from scratch again too." Of course it did
  // — there was only ever one, and going back rebuilt it. It is also why nothing could be
  // preloaded: there was nowhere to put a second model.
  //
  // So each distinct MODEL now gets its own reveal, built once and kept. Switching is moving a host
  // element into the visible page, which is instant in BOTH directions. The ones you are not
  // looking at are `setSuspended(true)` — they hold their context but render nothing, so the cost
  // is memory rather than frames.
  //
  // FOUR contexts worst case, against a browser limit of 8–16. The mobile budget this spends
  // against (<50k tris, one 2048 texture) is about what is DRAWN per frame, and only one of these
  // ever draws — so the budget is still met while the wait disappears.
  // Hosts are built HERE, off-screen but at a real size. A detached div is 0x0, and a renderer
  // built into nothing has nothing to snapshot — the still would come back blank.
  const oven = h('div', { class: 'wd-kp__oven', 'aria-hidden': 'true' });
  // ── THE OVEN MUST BE THE CARD, TO THE PIXEL (Ryan 2026-07-26) ────────────────────────────────
  // A still is rendered at whatever box its host is sitting in, then displayed `object-fit: contain`
  // in the card — so if the two boxes differ, the still is letterboxed AND was framed for a
  // different aspect, and the live canvas that fades in over it is neither. `fitCamera` puts the
  // camera at `faceZOffset + fitRadius/(FILL·min(1,aspect)·tan)`; for a disc `faceZOffset` is 0 and
  // the aspect term cancels out of the on-screen width, but a BAR's branded face sits half a bar
  // length off the origin, so that term dominates and a change of aspect really does resize the
  // head. Measured: a 340×520 oven against a 338×435 card renders the head at 0.85× — which is
  // exactly the "loads smaller, then snaps to full size" on the two rubber bars.
  //   It has to be MEASURED, and measuring has to WAIT. This used to run once at construction,
  // where `el` is still detached and every box is 0 — the guard rejected it and the oven silently
  // kept its CSS 340×520 forever. Which products it hit was then a race: a still captured while its
  // host still sat in the oven was wrong, one captured after the host had been moved into a card
  // was right. The two rubber bars are the last two the preloader builds, so they are the two the
  // buyer has not landed on yet — hence "only the rubber bars", with no geometry story needed.
  // (The earlier fix chased the ENTRANCE, which is a CSS `scale()` on the host and cannot reach the
  // drawing buffer `snapshotPNG` reads. It was never going to move this number.)
  let ovenW = 0;
  let ovenH = 0;
  let markOvenReady;
  const ovenReady = new Promise((resolve) => { markOvenReady = resolve; });
  function sizeOvenToCard() {
    const art = pages[0] && pages[0].querySelector('.wd-kp__art');
    // clientWidth/Height, NOT getBoundingClientRect: the art box has a 1px border under
    // `box-sizing: border-box`, so its border box is 2px larger than the box the canvas fills.
    // The oven has no border, so copying the border box would bake in a small aspect error.
    const w = art ? art.clientWidth : 0;
    const ht = art ? art.clientHeight : 0;
    if (w < 40 || ht < 40 || (w === ovenW && ht === ovenH)) return;
    const first = !ovenW;
    ovenW = w; ovenH = ht;
    oven.style.width = `${w}px`;
    oven.style.height = `${ht}px`;
    if (first) { markOvenReady(); return; }
    // The card changed shape (rotation, a dynamic-viewport step). Every still on file was rendered
    // for the old one, so re-take the ones we still can: a live reveal has already re-fit itself to
    // the resized oven, making this a snapshot rather than a rebuild. Evicted keys have no context
    // to shoot; they render correctly when they are next built.
    for (const [key, entry] of revealByKey) captureStill(key, entry);
  }
  // KEYED BY PRODUCT KEY, not model. Every kind has its own geometry — Training Rubber is
  // `economy: true` (no zinc hub) where the Competition plates have one, EZ-curl is not a straight
  // bar, urethane is not rubber — so caching by MODEL served whichever kind happened to build
  // first to all of its siblings. That is exactly what Ryan hit: "the training bumper is pulling in
  // the wrong model… it has a thick central metal hub" and "all of these weights feature a steel
  // hub, as taken from the competition bumpers".
  const revealByKey = new Map();     // product key -> { host, reveal, used }
  const stillByKey = new Map();      // product key -> object URL of its rendered still
  let useTick = 0;
  let stageSerial = 0;
  let currentModel = '';
  let building = false;
  let queuedIdx = null;
  let lastScrollAt = 0;   // the preloader stands down while this is recent — see waitForQuietScroll
  let lastReconcile = 0;
  let settleTimer = null;
  let disposed = false;
  const textureCache = new Map();   // per MODEL — the blank face texture genuinely is shared
  let chunks = null;
  function loadChunks() {
    if (!chunks) chunks = Promise.all([import('../reveal-3d.js'), import('../texture-pipeline.js')]);
    return chunks;
  }
  /* ── NOTHING HEAVY UNTIL THE CALLER SAYS THE SCREEN HAS SETTLED (Ryan 2026-08-10) ───────────────
   * "there's too much trying to load at once, and the button animation becomes really laggy."
   *
   * A geometry build is SYNCHRONOUS and owns the main thread for its whole duration — the numbers
   * are at the top of this file (canvas at ~310ms, first frame at ~2.1s, "nothing else runs in that
   * gap"). Anything animating across those frames does not degrade, it stops. The gate's
   * Resume→Back morph is a `width` tween, which is layout and therefore main-thread by definition,
   * so it is the most exposed thing the app could possibly put next to this.
   *
   * ⚠ IT GATES THE BUILD, NOT THE PAGER. Pages, dots, the swipe, the wheel and the stills are all up
   * at once and behave exactly as before; the buyer can swipe through the whole set during the hold.
   * The card they are on shows the loader, which is what the wheel rule asks for anyway.
   * ⭐ ONE await covers every route in, because they all funnel through `buildProduct` — the demand
   * path, the preloader and `reconcile` alike.
   * ⛔ THE RELEASE IS THE CALLER'S PROBLEM, and it must be unconditional: a hold that never settles
   * is a pager with no 3D in it at all (gate.js races the morph against a hard timeout for exactly
   * this reason).
   * ⚠ Deliberately NOT in front of `loadChunks` — the network fetch is off-thread and wants the
   * hold's ~450ms, not the time after it. Its module evaluation is main-thread, but the gate has
   * already prefetched both chunks a beat after it opened, so by here it is a cache hit. */
  const holdUntil = opts.holdUntil || null;

  // ── THE WHEEL HAS EXACTLY ONE RULE (Ryan 2026-07-27) ────────────────────────────────────────────
  // "ANY time there's a background with no model, we need the loading animation.. and then ANY time
  // there's a model we must ensure there's NO loading animation."
  //
  // Both halves had been drifting, because the wheel was being driven by EVENTS — a build starting,
  // a promise resolving, a page being arrived at — and every one of those is a proxy for the real
  // question, which is simply: is there a product on this card right now? Proxies go stale (a build
  // that resolves before its canvas draws, a rebuilt reveal a per-key flag still calls drawn) and
  // each stale one produced one of the two symptoms.
  //
  // So the wheel is no longer set by anything that happens. It is DERIVED, from the two DOM facts
  // that decide what the buyer can see, and it is re-derived on every event that could change them
  // plus a cheap watchdog for any this file forgets.
  //   There is also NO minimum-visible window any more. It existed to stop a sub-100ms flash, and
  // it is exactly what made the wheel "linger for a second or so" after the model appeared — which
  // is the second half of the rule, and the rule wins.
  function pageShowsProduct(idx) {
    const page = pages[idx];
    if (!page) return true;   // no page, nothing to cover
    // `.is-live` is the ONLY thing that makes the stage opaque, and it is set solely on a real draw
    // — so it means "a drawn canvas is on top". Without it the stage is transparent and the still,
    // if it has loaded, is what shows.
    // …and VERIFY it, rather than taking the class's word for it. `is-live` is a claim that a drawn
    // canvas is on top; a claim can outlive its subject, and when it does the wheel stays down over
    // a grey card, which is the exact failure this rule exists to prevent. A canvas in the stage is
    // the cheapest possible check that the claim is still true.
    if (page.classList.contains('is-live') && page.querySelector('.wd-kp__stage canvas')) return true;
    const img = page.querySelector('.wd-kp__still');
    return !!(img && img.naturalWidth > 0 && img.classList.contains('is-on'));
  }

  /**
   * NO ARGUMENT, deliberately. It used to take the page to sync, and callers passed whichever page
   * they happened to be working on — a build finishing for page 6 while the buyer sat on page 7
   * would arm 6's wheel and, because this also clears every OTHER page, strip 7's. The wheel is a
   * property of THE PAGE YOU ARE ON and of nothing else, so there is only one page it can ever mean.
   */
  function syncWheel() {
    const idx = shownIdx;
    const page = pages[idx];
    if (!page || disposed) return;
    page.classList.toggle('is-loading', !pageShowsProduct(idx));
    // Never leave one spinning on a page the buyer has left.
    pages.forEach((pg, i) => { if (i !== idx) pg.classList.remove('is-loading'); });
  }
  /**
   * Resolve once the browser has actually PAINTED.
   *
   * A spinner shown immediately before a synchronous build is a spinner nobody sees: geometry is
   * built on the main thread, so between `classList.add('is-loading')` and the model appearing
   * there is no frame in which the browser could draw it. It therefore first painted as the build
   * ENDED — and by then `LOADER_MIN_MS` had long expired, so it was removed almost at once. Ryan
   * 2026-07-26: "when you swipe onto a model and see just the gray initially, there's no load
   * wheel… only when [the zoom-out] is happening do you see the loading wheel — so it lasts for
   * like 0.2 seconds." Yielding two frames first costs ~32ms of a ~1s build and is the difference
   * between a wait that is explained and a wait that is not.
   */
  const painted = () => new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

  // ── WHEN IS A CARD ACTUALLY SHOWING SOMETHING? ──────────────────────────────────────────────────
  // Not when `ensureProduct` resolves. Measured on the first card: the canvas element exists at
  // ~310ms and the first frame reaches it at ~2.1s — a blank card for 1.8s, because the first draw
  // has to compile shaders and upload the texture while the preloader is building the next model on
  // the same thread. The wheel used to come down at the 310ms mark, so the wait it existed to
  // explain happened entirely after it had gone. `onFirstRender` is the honest signal.
  // DRAWN IS A PROPERTY OF THE CANVAS, NOT OF THE PRODUCT. This was a Set of product keys, and a
  // key never un-draws — so after a reveal was evicted and rebuilt, the key still said "drawn"
  // while the new canvas was blank. `revealLive` believed it and made the stage opaque at once,
  // which put a grey rectangle over a perfectly good still, and the wheel logic believed it too and
  // armed nothing. That is Ryan's rubber bars: the last products built are the most evicted and
  // rebuilt, so they are where a per-KEY flag goes stale first.
  function markDrawn(entry) {
    if (!entry || entry.drawn) return;
    entry.drawn = true;
    const waiting = entry.drawWaiters;
    entry.drawWaiters = [];
    waiting.forEach((resolve) => resolve(true));
    syncWheel();   // a frame just landed — the card may no longer need covering
  }
  /** Resolve TRUE when THIS reveal has put a frame on screen, FALSE if it gave up waiting. */
  function whenDrawn(entry, ms = 5000) {
    if (!entry) return Promise.resolve(false);
    if (entry.drawn) return Promise.resolve(true);
    return new Promise((resolve) => {
      entry.drawWaiters.push(resolve);
      setTimeout(() => resolve(false), ms);
    });
  }

  // In-flight builds, keyed the same way. Without this, `showProductAt` and `preloadAll` can both
  // be inside a build for the SAME key — the `revealByKey.has` check below only catches a rival
  // that has already finished — and the loser's reveal is orphaned: a live WebGL context nothing
  // holds a reference to, which no eviction can reclaim and no `suspendOthers` can quiet. Handing
  // out the same promise makes a second request free instead of merely usually harmless.
  const buildingByKey = new Map();

  /** The key the buyer is looking at right now. */
  function currentKey() {
    const it = items[shownIdx];
    return it ? productKey(it.category, it.kind) : null;
  }

  /** Build (or fetch from cache) the reveal for one model. Never disposes; that is the point. */
  function ensureProduct(key, onDemand = false) {
    const hit = revealByKey.get(key);
    if (hit) { hit.used = ++useTick; return Promise.resolve(hit); }
    let inFlight = buildingByKey.get(key);
    if (!inFlight) {
      inFlight = buildProduct(key, onDemand).finally(() => buildingByKey.delete(key));
      buildingByKey.set(key, inFlight);
    }
    // RESOLVE TO WHAT IS CURRENT, not to the object that was built. A build takes ~a second and
    // several callers await the same promise, so the entry can be retired before every one of them
    // has had its turn — and `mountOnPage` on a retired entry re-inserts its host, which
    // `reveal.dispose()` has already emptied. That is a `.wd-kp__stage` with nothing inside it,
    // sitting opaque over the still: a grey card, with the wheel down because everything believed
    // a model was mounted. Observed as two stages on one page, `8:…/0` next to `26:…/1`.
    return inFlight.then((built) => {
      const current = revealByKey.get(key);
      if (current) return current;
      return built && !built.dead ? built : null;
    });
  }

  async function buildProduct(key, onDemand) {
    const model = modelFor(key);
    // ABANDON A BUILD THE BUYER HAS SWIPED PAST. The geometry build below is synchronous and cannot
    // be interrupted once it starts, so the only chance to not spend it is BEFORE it starts — and
    // an on-demand build has one meaningful reason to exist: the card in front of the buyer. Swipe
    // 3 → 5 → 7 quickly and, without this, the model you are finally sitting on waits behind two
    // builds for cards you left seconds ago. That queue is where "it takes 5-10 seconds" comes from.
    //   Only for `onDemand`: a PRELOAD is speculative by definition and its whole job is to run
    // while you are elsewhere.
    const abandoned = () => onDemand && currentKey() !== key && !revealByKey.has(key);
    // Never build into an unmeasured oven — a still rendered at the wrong box is the rubber-bar
    // bug, and this is the one place that can guarantee it cannot happen.
    const [[{ createReveal }, pipeline]] = await Promise.all([loadChunks(), ovenReady, holdUntil]);
    if (disposed || abandoned()) return null;
    const weight = defaultWeightFor(key);
    let texture = textureCache.get(model);
    if (!texture) {
      // Blank: the gate asks WHICH EQUIPMENT, and a plate wearing a sample design answers a
      // different question.
      const blank = createDefaultProfile();
      blank.elements = [];
      // NOT `buildTextureFromDesignProfile` — that wrapper forwards only `weight` and `size` and
      // DROPS `product`, so every model was handed a default BUMPER face: a plate ring with a hub
      // painted onto dumbbells, grip plates and bars alike. That is the steel hub Ryan kept seeing
      // on products that have no hub. main.js has always gone straight to `renderDesignToSVGString`
      // for exactly this reason; this now does the same.
      const svg = renderDesignToSVGString(blank, {
        weight,
        size: CARD_TEXTURE_PX,      // a card, not the designer's full-res stage
        uid: `wd-kp-${model}`,
        chrome: false,
        product: model,             // WHERE the slots land on this product's face
        // A product that cannot be dyed gets a black ground regardless of any plate colour.
        bodyColorOverride: productSupportsBodyColor(key) ? undefined : DEFAULT_BODY_COLOR,
      });
      const raster = await pipeline.rasterizeSVG(svg, { size: CARD_TEXTURE_PX });
      texture = pipeline.createThreeTextureFromRaster(raster);
      textureCache.set(model, texture);
    }
    if (disposed || abandoned()) return null;
    if (revealByKey.has(key)) return revealByKey.get(key); // a concurrent call won
    const host = h('div', { class: 'wd-kp__stage' });
    // Serial + key on the element itself. `reveal.dispose()` empties this div without removing it
    // (it only owns what it put INSIDE), so a leftover stage is invisible in a DOM dump unless you
    // can tell which build it came from.
    host.dataset.wdStage = `${++stageSerial}:${key}`;
    oven.appendChild(host);   // give it a box before the renderer measures one
    const geom = { ...(geomOptsFor(key) || {}), ...CARD_DETAIL[model] };
    // Built BEFORE the reveal, so `onFirstRender` can mark THIS instance rather than the product.
    const entry = { host, reveal: null, used: ++useTick, drawn: false, drawWaiters: [] };
    const reveal = createReveal({
      mountEl: host,
      persistent: true,
      getTexture: () => texture,
      product: model,
      plate: model === 'plate' ? geom : {},
      dumbbell: (model === 'dumbbell' || model === 'barbell') ? geom : {},
      grip: model === 'grip' ? { ...geom, weight } : {},
      onFirstRender: () => markDrawn(entry),
      // A LOST CONTEXT IS A DEAD ENTRY, not a paused one. The canvas stays in the DOM looking like
      // a mounted model — which is why the card went grey and `pageShowsProduct` still called it
      // covered, so no wheel appeared either — and if the browser evicted it for being over the
      // page limit, no `restored` event is coming. Retiring it makes the truth visible (the still
      // comes back, or the wheel does) and hands the rebuild to `reconcile`, which is already
      // watching for exactly this: a page whose model is missing.
      onContextLost: () => { retire(key, revealByKey.get(key) === entry ? entry : null); },
      restYaw: (REST_YAW_DEG[model] || 0) * DEG,
      facePivot: FACE_PIVOT[model] || 0,
      idleBobScale: IDLE_BOB_SCALE[model] == null ? 1 : IDLE_BOB_SCALE[model],
      fitScale: FIT_SCALE[model] || 1,
      // A pinch zooms, on centre, and that is all: this card is a choice, not an inspection.
      simpleZoom: true,
      // …and not far. There is nothing to inspect YET — every model here wears a blank black face,
      // since the buyer has not designed anything at the point of choosing what to design on. Deep
      // zoom on an undecorated product only shows tessellation. `1.15` = the face may grow to a
      // little larger than the card, which is enough to look at the piece and no further.
      maxFaceFill: 1.15,
    });
    if (reveal.isSupported === false) {
      // The browser REFUSED a WebGL context — which is what a page at its per-page limit does, and
      // therefore what the LAST products built run into. Returning null here left the orphan host
      // in the oven and nothing mounted on the card, so the page fell back to its still: an <img>
      // that looks exactly like a settled model and cannot be dragged (Ryan 2026-07-26: "when you
      // click and drag your finger on the image to move the 3D model around, nothing happens…
      // only noticed it on the fixed barbells" — the longest list, built last). The caller frees a
      // slot and asks again; at least clean up after ourselves.
      host.remove();
      return null;
    }
    reveal.open();
    // NO POST-FIT DOLLY here — see the note at the top of the file. It was overwritten by the
    // ResizeObserver's first `fitCamera`, and the only thing it ever produced was the zoom-out.
    entry.reveal = reveal;
    revealByKey.set(key, entry);
    captureStill(key, entry);   // fire and forget — the live canvas is already usable
    evictLive();
    return entry;
  }

  /**
   * Keep the live set small. A reveal past the ring is holding a WebGL context to render something
   * nobody is looking at, and ten of those is how a phone runs out of contexts.
   *
   * ── THE STILL IS THE PRECONDITION, NOT A CONSEQUENCE ────────────────────────────────────────────
   * This used to be a plain LRU over `used`, which is wrong twice over, and both showed up the
   * moment there were more than MAX_LIVE products — i.e. with four categories selected and never
   * with one (Ryan 2026-07-26: "everything works just about perfectly when I only start with 1
   * category like this").
   *
   *  1. IT EVICTED THE MODEL ON SCREEN. `mountOnPage` marks the visible entry most-recently-used,
   *     but the preloader then builds seven more, each bumping the clock, so the card you are
   *     LOOKING AT becomes the oldest and gets disposed under you: "less than a second after the
   *     model appears it disappears again and all you see is gray for 1-1.5 seconds", then the
   *     post-preload re-assert rebuilt it. Recency cannot express "in use"; being mounted can.
   *  2. IT EVICTED MODELS BEFORE THEIR STILL EXISTED. A still is taken ~1.3s after the build, and
   *     the preloader starts the next one after ~250ms — so a disposed reveal took its unwritten
   *     still with it, and that card had nothing to show when you first swiped to it. That is the
   *     "gray for 0.5s, then the spinner, then the model" on the first pass through the other nine.
   *
   * So the ring drains as stills LAND (captureStill calls this again) rather than as builds start.
   * The live set can sit a little over MAX_LIVE while snapshots are outstanding, which is the cost
   * of every card having a picture — bounded by HARD_CEILING so a failing snapshot cannot let it
   * grow without limit.
   */
  const HARD_CEILING = MAX_LIVE + 2;

  /**
   * Give up ONE context, for a build that is happening on demand — the buyer is on that card now.
   *
   * Deliberately harsher than `evictLive`: it ignores the still rule and will take a host mounted
   * on some other page, because the only thing that outranks "the card in front of the buyer can be
   * touched" is nothing. It still never takes the current model. Without this there is no way out
   * of a full context table: every candidate `evictLive` is willing to take can be unavailable at
   * once (a page you visited holds its host until its still lands, and a still that never gets
   * taken holds it forever), and then every later build is refused and every later card is a
   * picture you cannot drag.
   */
  function freeOneSlot() {
    let victim = null;
    let oldest = Infinity;
    for (const [k, e] of revealByKey) {
      if (k === currentModel) continue;
      if (e.used < oldest) { oldest = e.used; victim = k; }
    }
    if (victim == null) return false;
    retire(victim, revealByKey.get(victim));
    return true;
  }

  /**
   * The ONE way a reveal goes away. Disposal is three things that have to happen together, and
   * every place that did them by hand got at least one wrong: mark the entry dead (so a caller
   * still holding it cannot mount a husk), take the stage out of the DOM (`reveal.dispose()` only
   * removes what it put INSIDE the stage, leaving an empty opaque div behind), and — if it was
   * mounted — drop `is-live` from that page, because that class is what tells everything else the
   * card has a drawn canvas on it.
   */
  function retire(key, entry) {
    if (!entry) return;
    const page = entry.host.parentElement && entry.host.parentElement.closest('.wd-kp__page');
    entry.dead = true;
    try { entry.reveal.dispose(); } catch { /* already gone */ }
    entry.host.remove();
    if (revealByKey.get(key) === entry) revealByKey.delete(key);
    if (page) page.classList.remove('is-live');
    syncWheel();
  }
  function evictLive() {
    if (revealByKey.size <= MAX_LIVE) return;
    const desperate = revealByKey.size > HARD_CEILING;
    const candidates = [];
    for (const [k, e] of revealByKey) {
      if (k === currentModel) continue;                                   // the buyer is looking at it
      if (e.host.parentElement && e.host.parentElement !== oven) continue; // mounted on a page
      if (!desperate && !stillByKey.has(k)) continue;                     // its picture isn't taken yet
      candidates.push([k, e]);
    }
    candidates.sort((a, b) => a[1].used - b[1].used);   // genuinely least-recently-used, among those free to go
    let over = revealByKey.size - MAX_LIVE;
    for (const [k, e] of candidates) {
      if (over-- <= 0) break;
      retire(k, e);
    }
  }

  /**
   * Snapshot a built model and hang it on every page that shows it.
   *
   * This is what makes a fast flick look continuous: the still is the same render the live canvas
   * produces, so a card entering the screen already has its product on it and the swap to the live
   * one is invisible. Deliberately AFTER two frames — `snapshotPNG` reads the drawing buffer, and
   * asking before the first render returns an empty one.
   */
  async function captureStill(key, entry) {
    try {
      // WAIT FOR THE SWAY TO TURN IT. `persistent` opens FACE-ON and then idles into a slow
      // rotation, so a snapshot on the next frame is the flat, straight-on pose. Capturing at a
      // fixed point in that motion means the still is already angled and already zoomed, and every
      // product is captured at the same phase — so the cards share one house angle.
      //
      // …but WAIT FOR THE ENTRANCE FIRST. `.wd-reveal-3d` fades in from `scale(0.985)` over
      // --dt-duration-slide, and `is-revealed` is only added once the build finishes. A fixed delay
      // from `open()` therefore races the build: the two RUBBER bars are the heaviest geometry in
      // the set (EZ-curl ~30k tris), so their snapshot landed while the entrance was still scaling
      // up — the still froze a slightly smaller head and the live canvas then "snapped" to full
      // size (Ryan 2026-07-26). Every lighter product finished in time, which is why only those two
      // showed it. Waiting for the class makes the timing depend on the model being READY rather
      // than on a guess about how long it takes.
      await new Promise((resolve) => {
        const started = performance.now();
        const tick = () => {
          if (disposed) return resolve();
          const ready = entry.host.querySelector('.wd-reveal-3d.is-revealed');
          // 2500ms ceiling so a model that never reports ready still yields a still rather than none.
          if (ready || performance.now() - started > 2500) return resolve();
          requestAnimationFrame(tick);
        };
        tick();
      });
      if (disposed) return;
      // Then the entrance transition itself, and only then the pose delay.
      await new Promise((r) => setTimeout(r, ENTRANCE_MS + STILL_POSE_MS));
      if (disposed) return;
      const blob = await entry.reveal.snapshotPNG?.();
      if (!blob || disposed) return;
      const url = URL.createObjectURL(blob);
      const prev = stillByKey.get(key);   // a re-shoot after a card resize replaces one
      stillByKey.set(key, url);
      // ONE page per key now — each kind has its own geometry, so each gets its own render.
      items.forEach((it, i) => {
        if (productKey(it.category, it.kind) !== key) return;
        const img = pages[i] && pages[i].querySelector('.wd-kp__still');
        if (img) {
          // `decode()` before showing it: setting .src does not mean the <img> can paint, and
          // `naturalWidth` — which is how `pageShowsProduct` decides the card is covered — goes
          // non-zero at header parse, a beat before the pixels exist. Marking it on early is a card
          // that reports a picture it cannot yet draw, i.e. grey with the wheel already down.
          img.src = url;
          const show = () => {
            img.classList.add('is-on');
            pages[i].classList.add('has-still');   // there is now something to cross-fade FROM
            syncWheel();
          };
          if (img.decode) img.decode().then(show, show); else img.onload = show;
        }
      });
      // AFTER the swap, so nothing is still pointing at it when it goes.
      if (prev) { try { URL.revokeObjectURL(prev); } catch { /* fine */ } }
      // This reveal has now left something behind. Two things were waiting on exactly that: the
      // host can go back to the oven (it was held on its card because nothing else was covering
      // it), and it — or one of its elders — is free to be evicted. See parkIfCovered / evictLive.
      const held = revealByKey.get(key);
      if (held && key !== currentModel) { held.reveal.setSuspended?.(true); parkIfCovered(key, held); }
      evictLive();
    } catch { /* no still — the live canvas still works on the settled page */ }
  }

  /** Put a built model on its page and make it the live one. The single place that decides what the
   *  buyer is looking at, so mounting, waking and suspending can never disagree. */
  function mountOnPage(idx, key, entry) {
    const dest = pages[idx] && pages[idx].querySelector('.wd-kp__art');
    if (!dest || !entry || entry.dead) return;   // never re-insert a host dispose has already gutted
    entry.used = ++useTick;
    reconcileAt.delete(key);
    if (entry.host.parentElement !== dest) dest.appendChild(entry.host);
    // ALWAYS, even for a reveal that was never suspended: it has just been reparented out of an
    // off-screen oven, and its own IntersectionObserver may have parked the loop while it sat
    // there. `setSuspended(false)` now repaints unconditionally (reveal-3d), so this is the kick
    // that guarantees a mounted card draws rather than staying on the empty backdrop.
    entry.reveal.setSuspended?.(false);
    suspendOthers(key);
    currentModel = key;
    revealLive(idx, entry);
    syncWheel();
  }

  /** Show the model for page `idx`, building it only if this is its first showing. */
  async function showProductAt(idx) {
    const it = items[idx];
    if (!it || disposed) return;
    const key = productKey(it.category, it.kind);
    const art = pages[idx] && pages[idx].querySelector('.wd-kp__art');
    if (!art) return;

    const cached = revealByKey.get(key);
    if (cached) {
      // Built already — mounting is instant in both directions. That does NOT mean there is
      // anything to look at: the preloader builds ahead of the buyer, so the common case for the
      // FIRST card is a cache hit onto a canvas that has not drawn yet.
      mountOnPage(idx, key, cached);
      syncWheel();
      return;
    }
    // A build is already running. Do NOT drop this request: the page the buyer is on is the one
    // fact this component exists to honour, and a dropped request left it on the bare backdrop with
    // nothing scheduled to try again. Remember it and run it when the current build lets go.
    if (building) { queuedIdx = idx; return; }
    building = true;
    syncWheel();
    let entry = null;
    try {
      // Let the wheel reach the screen BEFORE the synchronous geometry build takes the thread.
      await painted();
      // The preloader can afford to WAIT for room (waitForContextRoom); this path cannot, so take
      // the still-safe eviction first and only get harsh if the build is actually refused.
      if (!disposed) evictLive();
      // `true` = on demand: a card is waiting on this one, so it may be abandoned if the buyer
      // swipes away before the expensive part starts.
      if (!disposed) entry = await ensureProduct(key, true);
      if (!entry && !disposed && currentKey() === key && freeOneSlot()) entry = await ensureProduct(key, true);
    } catch {
      entry = null;   // the studio backdrop stands in
    }
    building = false;
    if (disposed) return;
    if (entry) mountOnPage(idx, key, entry);

    // A page requested mid-build has been waiting: hand over to it rather than holding the wheel
    // here for a card the buyer has already swiped past.
    const next = queuedIdx;
    queuedIdx = null;
    if (next != null && next !== idx) { showProductAt(next); return; }

    // Nothing here re-tries a failed build. `reconcile` does, from the frame loop, for as long as
    // the card is still the one on screen — a counter that gives up after N goes is how a card ends
    // up permanently dead while looking fine (see reconcile).
    // The wheel is derived, not set — `markDrawn` and `revealLive` re-derive it as the frame lands.
    syncWheel();
  }

  /**
   * THE SUPERVISOR. Every bug in this file that survived a round has had the same shape: a chain of
   * events — scroll fires, settle timer runs, build resolves, entry mounts — where dropping any one
   * link leaves a card permanently wrong, and the code then has no idea anything is missing. The
   * still makes that invisible: a card with no live reveal looks exactly like a loaded one until
   * you put a finger on it (Ryan 2026-07-27: "1 or 2 weights… when I put my finger on the weight
   * and try to pan around it, nothing happens… no loading wheel when these ones are waiting").
   *
   * So the invariant stops being something the event chain has to deliver and becomes something a
   * loop keeps checking: THE PAGE YOU ARE ON HAS A LIVE MODEL MOUNTED. If it does not, and nothing
   * is already on its way, ask for one. Same reasoning that fixed the wheel, applied to the thing
   * the wheel was describing. It also replaces the three-strikes retry counter, which could only
   * ever end in a card that had given up.
   *
   * The backoff is the whole safety story: a build that keeps failing is re-tried about once a
   * second, forever, but never faster — so a genuinely broken device costs one attempt per second
   * rather than a spin loop, and a device that recovers heals on its own without a swipe.
   */
  const reconcileAt = new Map();   // key -> when we last asked, so a hard failure cannot spin
  function reconcile(now) {
    if (disposed || building) return;
    const idx = shownIdx;
    const it = items[idx];
    if (!it) return;
    const key = productKey(it.category, it.kind);
    const art = pages[idx] && pages[idx].querySelector('.wd-kp__art');
    const entry = revealByKey.get(key);
    if (entry && !entry.dead && art && entry.host.parentElement === art) return;  // all well
    if (buildingByKey.has(key)) return;                                           // already coming
    if (now - (reconcileAt.get(key) || 0) < 1100) return;
    reconcileAt.set(key, now);
    showProductAt(idx);
  }

  /** Cross-fade the live canvas in over its own still, so the handover is a dissolve rather than a
   *  jump. The still is a single frame of a model that never stops moving, so the two can never be
   *  at the identical pose — Ryan: "you see the model then it snaps to a slightly different model".
   *  A short fade turns an unavoidable difference into an intentional-looking transition.
   *
   *  ONLY ONCE THIS REVEAL HAS DRAWN. The class is what makes the stage opaque, and the stage
   *  carries a backdrop — so revealing it before a frame lands takes a card that was showing its
   *  still and turns it grey until the canvas catches up. `entry.drawn`, never a per-product flag:
   *  a rebuilt reveal starts blank however many times the product has been drawn before. */
  function revealLive(idx, entry) {
    pages.forEach((pg, i) => { if (i !== idx) pg.classList.remove('is-live'); });
    const page = pages[idx];
    if (!page || !entry) return;
    if (entry.drawn) { page.classList.add('is-live'); syncWheel(); return; }
    whenDrawn(entry).then((drew) => {
      if (drew && !disposed && revealByKey.get(currentModel) === entry) page.classList.add('is-live');
      if (!disposed) syncWheel();
    });
  }

  /**
   * Send a host back to the oven — but ONLY once its still can stand in for it.
   *
   * The old rule sent it back unconditionally, on the reasoning that "the still already covers that
   * card". True for every product except the one case that matters: the FIRST card, whose still is
   * taken ~1.3s after its build. Swipe off it inside that window and the host is pulled out from
   * under a card with nothing behind it, so the product blinks out mid-swipe — and only ever on the
   * first one, exactly as Ryan found ("it only happens on the first model, not any of the other 9").
   * A suspended reveal left in place keeps its last frame on screen, which is the same picture the
   * still would have been; `parkStill` collects it as soon as there is one.
   */
  function parkIfCovered(key, entry) {
    if (!stillByKey.has(key)) return;
    if (entry.host.parentElement && entry.host.parentElement !== oven) oven.appendChild(entry.host);
  }

  function suspendOthers(keepKey) {
    for (const [m, e] of revealByKey) {
      if (m === keepKey) continue;
      e.reveal.setSuspended?.(true);
      parkIfCovered(m, e);
    }
  }

  function scheduleStage() {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(() => {
      const idx = Math.round(track.scrollLeft / pageStride());
      showProductAt(Math.max(0, Math.min(items.length - 1, idx)));
    }, 200);
  }

  /**
   * Hold the preloader while the buyer is actually swiping.
   *
   * A geometry build blocks the main thread for its whole duration, and the preloader was starting
   * one every ~250ms regardless of what the thumb was doing — so the harder you swiped, the more
   * certain you were to land on a frozen screen. That is the asymmetry Ryan described: a single
   * calm swipe has always worked, and fast or back-and-forth swiping is what breaks. The buyer's
   * gesture outranks speculative work every time, so the preloader steps aside until the track has
   * been still for a beat, then carries on where it left off.
   */
  async function waitForQuietScroll() {
    for (let i = 0; i < 200; i++) {
      if (disposed) return false;
      // …and never while a card the buyer is ON is being built. `building` guards the demand path
      // only, so the preloader could start its own thread-blocking build in the middle of one,
      // which puts a speculative model ahead of the one somebody is actually waiting for.
      if (!building && performance.now() - lastScrollAt > 260) return true;
      await new Promise((r) => setTimeout(r, 100));
    }
    return true;   // a very long scroll should not cancel preloading outright
  }

  /**
   * Hold the preloader until there is room for one more live context — i.e. until an outstanding
   * still lands and lets `evictLive` drain. Returns false only if the pager went away while waiting
   * or the wait ran long, in which case preloading simply stops and the cards build on demand.
   */
  async function waitForContextRoom() {
    for (let i = 0; i < 80; i++) {
      if (disposed) return false;
      evictLive();
      if (revealByKey.size < MAX_LIVE + 1) return true;
      await new Promise((r) => setTimeout(r, 120));
    }
    return false;
  }

  /**
   * PRELOAD every model the buyer selected, one at a time, after the first is on screen.
   * Ryan: "it would be great if the app could detect the 4 cards you were selecting on the previous
   * screen, and begin loading the models."
   *
   * Sequential on purpose: four parallel geometry builds on a phone would stall the very swipe they
   * are meant to make smooth. Each waits for an idle moment, so the visible model keeps its frames.
   */
  async function preloadAll() {
    // EVERY KIND, because every kind is its own geometry. The reveals themselves are evicted down
    // to MAX_LIVE as this runs — what survives is the STILL, which is all a card off-screen needs.
    const wanted = items.map((it) => productKey(it.category, it.kind))
      .filter((k, i, a) => a.indexOf(k) === i);
    // TWO PASSES, because a build can now be abandoned mid-flight when the buyer swipes off the
    // card that asked for it — and an abandoned build leaves that product with no still, on a list
    // this loop has already walked past. The second pass costs nothing when the first one finished
    // the job: every key with a still is skipped outright.
    for (let pass = 0; pass < 2; pass++) {
    for (const key of wanted) {
      if (disposed) return;
      if (stillByKey.has(key)) continue;
      // A SHORT yield, not a patient one. `timeout: 900` waited up to ~0.9s between each of four
      // builds on top of the builds themselves — most of the delay before every card had a product
      // on it, which is the window Ryan was swiping through. 250ms still hands a frame back to the
      // visible model between builds without making the buyer wait on politeness.
      await new Promise((r) => (window.requestIdleCallback
        ? window.requestIdleCallback(r, { timeout: 250 })
        : setTimeout(r, 120)));
      if (disposed) return;
      // BACK-PRESSURE, not just eviction. Eviction now waits for stills, so builds that outrun
      // their snapshots pile contexts up — and a browser over its per-page WebGL limit does not
      // refuse the new one, it silently KILLS THE OLDEST, which is always the card the buyer landed
      // on first. That is the "screen goes gray for a second, then the model comes back stripped of
      // its lighter tones" (a lost context, restored without its environment map, so metalness 0.95
      // has nothing left to reflect). Waiting here costs the preloader a moment; the alternative
      // costs the buyer the model they are looking at.
      // `break`, not `return`: giving up on the REST of the preload is fine (those cards build on
      // demand), but the re-assert after this loop is what guarantees the page the buyer is on has
      // its model, and skipping that is how a card ends up waiting on nothing.
      if (!(await waitForContextRoom())) break;
      // …and stand down entirely while the thumb is moving. Checked AFTER the context wait, so a
      // swipe that begins during that wait still gets the thread.
      if (!(await waitForQuietScroll())) break;
      if (disposed) return;
      try {
        const entry = await ensureProduct(key);
        // Suspend on WHERE THE HOST IS, not on whether this key matches `currentModel`. The two say
        // the same thing only if `showProductAt` happened to run its continuation first; when the
        // preloader won that race it suspended the model the buyer was looking at, and nothing was
        // scheduled to wake it — a card that sits on the bare backdrop until you swipe away and
        // back (Ryan 2026-07-26: "the first model never loads, it stays gray"). A host still in the
        // oven has, by definition, not been mounted on a page by anyone.
        if (entry && entry.host.parentElement === oven) entry.reveal.setSuspended?.(true);
      } catch { /* skip it; it will build on demand */ }
    }
    }
    // The landed page has the last word. Preloading runs for seconds alongside everything else, so
    // rather than reason about every order those can finish in, re-assert the one invariant that
    // matters once the noise is over: the page you are on has its model, mounted and awake.
    if (!disposed) showProductAt(Math.max(0, Math.min(items.length - 1, shownIdx)));
  }

  let raf = 0;
  track.addEventListener('scroll', () => {
    lastScrollAt = performance.now();   // the preloader reads this and steps aside
    // rAF-coalesced: scroll fires far faster than paint, and the only work here is a class swap.
    if (raf) return;
    raf = requestAnimationFrame(() => { raf = 0; syncPosition(); });
  }, { passive: true });

  track.addEventListener('scroll', scheduleStage, { passive: true });

  paint();
  scheduleStage(); // the first page gets its model without waiting for a swipe
  // THE WATCHDOG. Every path that can change what a card shows re-derives the wheel, and the rule
  // Ryan set is absolute — a blank card must ALWAYS be covered, a card with a product must NEVER
  // be. An absolute rule should not depend on this file having remembered every path, now and after
  // the next change, so it is also re-derived on a timer. Two class checks; it costs nothing.
  // ON EVERY FRAME, not on a timer. A timer can only ever chase the truth: after the main thread is
  // blocked by a geometry build, whichever of the watchdog and the paint fires first decides what
  // the buyer sees, and at 80ms that lost often enough to show up as one bare frame per few runs.
  // `requestAnimationFrame` runs BEFORE the paint by definition, so the state on screen is correct
  // in every painted frame — which is the only place the rule can actually be broken. It costs two
  // class checks against a canvas that is already animating.
  let wheelRaf = requestAnimationFrame(function wheelTick(now) {
    syncWheel();
    // Cheaper than the wheel and doing more, so not every frame: three times a second is far faster
    // than a buyer can notice a missing model and slow enough to cost nothing.
    if (now - lastReconcile > 320) { lastReconcile = now; reconcile(now); }
    wheelRaf = requestAnimationFrame(wheelTick);
  });

  // Kick the warm off immediately — by the time the first page settles its texture is usually
  // already in the cache, and every later swipe skips the expensive half outright.
  el.appendChild(oven);
  // MEASURE ON MOUNT, not here: nothing in this function can see a real box, because `el` is still
  // detached — the gate appends it after we return. A ResizeObserver fires its first observation
  // the moment the card has a box, which is precisely the moment a one-shot call cannot reach, and
  // then keeps the oven in step through a rotation for free.
  let cardObserver = null;
  const artOfFirstPage = pages[0] && pages[0].querySelector('.wd-kp__art');
  if (typeof ResizeObserver !== 'undefined' && artOfFirstPage) {
    cardObserver = new ResizeObserver(() => sizeOvenToCard());
    cardObserver.observe(artOfFirstPage);
  } else {
    requestAnimationFrame(() => sizeOvenToCard());
  }
  // A backstop, not a schedule: if the card never reports a usable box, release the builds anyway
  // rather than leaving the pager with no 3D at all. The oven keeps its CSS size, which is the
  // behaviour we had before — degraded, but present.
  setTimeout(() => { sizeOvenToCard(); markOvenReady(); }, 1500);
  preloadAll();

  return {
    el,
    refresh: paint,
    /** The gate calls this when beat 2 goes away — a WebGL context left running behind a dead
     *  screen is the exact leak `setSuspended` exists to prevent, and dispose is stronger. */
    destroy() {
      disposed = true;
      clearTimeout(settleTimer);
      cancelAnimationFrame(wheelRaf);
      try { cardObserver?.disconnect(); } catch { /* never observed */ }
      // EVERY context, not just the visible one — holding several is the whole design here, so
      // letting several leak is the whole risk.
      for (const [, e] of revealByKey) { try { e.reveal.dispose(); } catch { /* already gone */ } }
      revealByKey.clear();
      for (const [, url] of stillByKey) { try { URL.revokeObjectURL(url); } catch { /* fine */ } }
      stillByKey.clear();
    },
  };
}
